"use client"
import { useCreditFormatters } from "@/hooks/use-credit-formatters"


import { useEffect, useState, useCallback } from "react"
import { Bot, CreditCard, Loader2, Users, Wallet } from "lucide-react"
import { Card, CardContent } from "@/components/ui/card"
import { useAuth } from "@/hooks/use-auth"

import type { TenantStatsResponse } from "@/app/api/tenants/current/stats/route"
import { useTranslations } from "next-intl"



/**
 * Read-only overview cards for the Organization tab — users count, active
 * agents, wallet balance, current plan. Data lives behind the
 * `tenants.view_own` permission (see the endpoint comment) so the tab is
 * only mounted for an authorised user; this component assumes the caller
 * has gated it correctly.
 */
export function OrganizationStats() {
  const { formatCredits } = useCreditFormatters()


  const tUi = useTranslations("Organization.OrganizationStats")

  const { fetchWithAuth } = useAuth()
  const [stats, setStats] = useState<TenantStatsResponse | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    setIsLoading(true)
    setError(null)
    try {
      const res = await fetchWithAuth("/api/tenants/current/stats")
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string }
        throw new Error(body.error || `Failed to load stats (${res.status})`)
      }
      const data = (await res.json()) as TenantStatsResponse
      setStats(data)
    } catch (e) {
      setError(e instanceof Error ? e.message : tUi("failedToLoadStats"))
    } finally {
      setIsLoading(false)
    }
  }, [fetchWithAuth, tUi])

  useEffect(() => {
    void load()
  }, [load])

  if (isLoading) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <Card key={i}>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-muted-foreground">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span className="text-xs"><span key={tUi("loading")}>{tUi("loading")}</span></span>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    )
  }

  if (error || !stats) {
    return (
      <Card>
        <CardContent className="p-4 text-sm text-destructive">
          {error ?? tUi("unableToLoadOrganizationStats")}
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      <StatCard
        icon={<Users className="w-4 h-4 text-blue-500" />}
        label={tUi("users")}
        value={String(stats.users_count)}
      />
      <StatCard
        icon={<Bot className="w-4 h-4 text-purple-500" />}
        label={tUi("activeAgents")}
        value={String(stats.active_agents.total)}
        sub={`${stats.active_agents.voice} voice · ${stats.active_agents.email} email · ${stats.active_agents.messaging} msg`}
      />
      <StatCard
        icon={<Wallet className="w-4 h-4 text-emerald-600" />}
        label={tUi("wallet")}
        value={formatCredits(stats.wallet_microusd)}
      />
      <StatCard
        icon={<CreditCard className="w-4 h-4 text-orange-500" />}
        label={tUi("plan")}
        value={stats.plan?.display_name ?? "—"}
        sub={stats.plan ? stats.plan.name : "No subscription"}
      />
    </div>
  )
}

function StatCard({
  icon,
  label,
  value,
  sub,
}: {
  icon: React.ReactNode
  label: string
  value: string
  sub?: string
}) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          {icon}
          <span>{label}</span>
        </div>
        <div className="mt-1.5 text-xl font-semibold leading-tight truncate">
          {value}
        </div>
        {sub && (
          <div className="text-xs text-muted-foreground truncate mt-0.5">
            {sub}
          </div>
        )}
      </CardContent>
    </Card>
  )
}
